import { getTableHeaderElementId } from "@/block-system/blocks/Table/Block/TableHeaderCell";
import {
  TABLE_ROW_ACTIONS_COLUMN_ID,
  TABLE_VERTICAL_SCROLLBAR_OFFSET_CSS_VARIABLE_NAME,
} from "@/block-system/blocks/Table/constants";
import { TableRecord } from "@/server/schemas/table-records";
import { cn } from "@/utils/cn";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type Header,
  type HeaderGroup,
  type OnChangeFn,
  type Row,
  type SortingState,
  type Table,
  useReactTable,
} from "@tanstack/react-table";
import {
  type CSSProperties,
  forwardRef,
  memo,
  useLayoutEffect,
  useMemo,
  useRef,
  useImperativeHandle,
} from "react";

export type TanstackTableImperativeAPI = {
  scrollToTop: () => void;
};

export const TanstackTable = forwardRef<
  TanstackTableImperativeAPI,
  {
    data: TableRecord[];
    columns: ColumnDef<TableRecord>[];
    isLoading: boolean;
    sortingState: SortingState;
    setSortingState: OnChangeFn<SortingState>;
    Pagination: React.ReactNode;
    onRowClick: (row: Row<TableRecord>) => void;
    enableSorting?: boolean;
    tableId: string;
    title: string | undefined;
  }
>(function TanstackTable(
  {
    title,
    columns,
    data,
    isLoading,
    sortingState,
    setSortingState,
    Pagination,
    onRowClick,
    enableSorting = true,
    tableId,
  },
  imperativeAPIRef
) {
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualFiltering: true,
    enableMultiSort: false,
    /**
     * `undefined` means that we should decide on column-by-column basis.
     */
    enableSorting: enableSorting ? undefined : false,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    initialState: {
      columnPinning: {
        right: [TABLE_ROW_ACTIONS_COLUMN_ID],
      },
    },
    state: {
      sorting: sortingState,
    },
    onSortingChange: setSortingState,
  });

  const tableRef = useRef<HTMLTableElement>(null);
  const { gridTemplateColumns, getResizeHandler } = useGridTemplateColumns({
    table,
    tableRef,
  });

  useImperativeHandle(
    imperativeAPIRef,
    () => ({
      scrollToTop: () => {
        /**
         * The RAF is here to ensure this function runs _after_ React had a chance to update the DOM.
         * If we did not use RAF here, the scroll would not work properly.
         */
        requestAnimationFrame(() => {
          tableRef.current?.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        });
      },
    }),
    []
  );

  /**
   * Since the table can have vertical scrollbar, the 100cqi which we use to size the table footer,
   * will be too wide – it includes the width of the scrollbar!
   *
   * We have to calculate how large the scrollbar + the borders are and offset our table footer width with that value.
   * If we do not, we will create a horizontal scrollbar in the table footer when it is not needed.
   */
  useLayoutEffect(() => {
    if (!tableRef.current) {
      return;
    }

    const scrollbarWidth =
      tableRef.current.offsetWidth - tableRef.current.clientWidth;

    tableRef.current.style.setProperty(
      TABLE_VERTICAL_SCROLLBAR_OFFSET_CSS_VARIABLE_NAME,
      `${scrollbarWidth}px`
    );
  }, [data, columns]);

  return (
    /**
     * We use the container query units to "escape" the CSS grid in the table footer.
     * We have to use `contain-layout` to ensure work properly. See https://github.com/radix-ui/primitives/issues/3143
     */
    <div
      className="contain-layout @container/table-container"
      id={getTableContainerElementId({ tableId })}
    >
      <table
        id={getTableElementId({ tableId })}
        ref={tableRef}
        className={cn(
          /**
           * The max height is arbitrary.
           */
          "max-h-[600px]",
          "relative grid gap-x-4",
          "w-full overflow-auto overscroll-contain rounded-large border border-border bg-card",
          "has-[[data-resizing='true']]:cursor-ew-resize has-[[data-resizing='true']]:select-none",
          "group/table"
        )}
        data-loading={isLoading}
        style={{
          gridTemplateColumns,
        }}
      >
        {title ? <caption className="sr-only">{title}</caption> : null}
        <TableHeader
          tableId={tableId}
          getResizeHandler={getResizeHandler}
          headerGroups={table.getHeaderGroups()}
        />
        <TableBody rows={table.getRowModel().rows} onRowClick={onRowClick} />
        {Pagination ? <TableFooter>{Pagination}</TableFooter> : null}
      </table>
    </div>
  );
});

function TableHeader({
  headerGroups,
  getResizeHandler,
  tableId,
}: {
  headerGroups: HeaderGroup<TableRecord>[];
  tableId: string;
  getResizeHandler: (
    header: Header<TableRecord, unknown>
  ) => (event: unknown) => void;
}) {
  return (
    <thead
      className={cn(
        "sticky top-0 z-[1]",
        "border-b border-border bg-card",
        "col-span-full grid grid-cols-[subgrid]"
      )}
    >
      {headerGroups.map((headerGroup) => {
        return (
          <tr
            className={
              "col-span-full grid h-[3.5rem] w-full grid-cols-[subgrid] items-center"
            }
            key={headerGroup.id}
          >
            {headerGroup.headers.map((header) => {
              const canResize = header.column.getCanResize();
              const isPinned = header.column.getIsPinned();
              const onResize = getResizeHandler(header);
              const isResizing = header.column.getIsResizing();
              const isActionsCell =
                header.column.id === TABLE_ROW_ACTIONS_COLUMN_ID;

              return (
                <th
                  data-column-id={header.column.id}
                  aria-labelledby={getTableHeaderElementId({ header, tableId })}
                  key={header.id}
                  style={{
                    ...getPinnedColumnStyles(isPinned),
                  }}
                  className={cn(
                    "grid h-full grid-cols-[auto_min-content] items-center bg-card first-of-type:pl-4 last-of-type:pr-4",
                    {
                      "w-14": isActionsCell,
                      "justify-center": isActionsCell,
                      "p-0": isActionsCell,
                      "-ml-4": isActionsCell,
                    }
                  )}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}

                  {canResize ? (
                    <TableColumnResizeHandle
                      onResize={onResize}
                      isResizing={isResizing}
                    />
                  ) : null}
                </th>
              );
            })}
          </tr>
        );
      })}
    </thead>
  );
}

/**
 * It is vital to keep this component memoized.
 * Otherwise, the UI will be choppy when resizing columns.
 */
function TableBodyImpl({
  rows,
  onRowClick,
}: {
  rows: Row<TableRecord>[];
  onRowClick: (row: Row<TableRecord>) => void;
}) {
  return (
    <tbody className={cn("col-span-full grid grid-cols-[subgrid]")}>
      {rows.map((row) => {
        return <TableBodyRow row={row} key={row.id} onRowClick={onRowClick} />;
      })}
    </tbody>
  );
}

const TableBody = memo(TableBodyImpl);

/**
 * As per Figma.
 */
const ACTIONS_COLUMN_SIZE = 56;

function TableBodyRow({
  row,
  onRowClick,
}: {
  row: Row<TableRecord>;
  onRowClick: (row: Row<TableRecord>) => void;
}) {
  return (
    <tr
      className={cn(
        "col-span-full grid grid-cols-[subgrid]",
        "border-b border-border last-of-type:border-none",
        "hover:bg-secondary",
        "transition-opacity",
        "group-data-[loading='true']/table:opacity-60",
        "group/table-row",
        /**
         * To ensure the row stays highlighted when "Actions menu" is open for this row.
         */
        "has-[[data-row-actions-open='true']]:bg-secondary"
      )}
      onClick={(event) => {
        /**
         * Do not trigger the row click if the user has selected text.
         */
        const selection = window.getSelection();
        const selectedText = selection?.toString() ?? "";
        const hasSelectedText = selectedText.length > 0;
        if (hasSelectedText) {
          return;
        }

        /**
         * Do not trigger the row click if the user has clicked on an interactive element.
         * The "interactive element" list is most likely not exhaustive.
         */
        const eventTargetPath = event.nativeEvent.composedPath();
        const hasInteractiveElementInPath = eventTargetPath.some((element) => {
          if (!(element instanceof HTMLElement)) {
            return false;
          }

          const isButton = element.tagName.toLowerCase() === "button";
          /**
           * Labels are tied to form elements / buttons (in case of a switch).
           */
          const isLabel = element.tagName.toLowerCase() === "label";
          const isLink = element.tagName.toLowerCase() === "a";

          return isButton || isLink || isLabel;
        });
        if (hasInteractiveElementInPath) {
          return;
        }

        onRowClick(row);
      }}
      key={row.id}
    >
      {row.getVisibleCells().map((cell) => {
        const isPinned = cell.column.getIsPinned();
        const isActionsCell = cell.column.id === TABLE_ROW_ACTIONS_COLUMN_ID;

        return (
          <td
            key={cell.id}
            onClick={(event) => {
              /**
               * Prevent miss-clicks on the "actions" cell.
               */
              if (isActionsCell) {
                event.stopPropagation();
              }
            }}
            style={{
              ...getPinnedColumnStyles(isPinned),
              width: isActionsCell ? ACTIONS_COLUMN_SIZE : undefined,
            }}
            className={cn(
              "flex min-h-14 items-center",
              "py-4 pr-4 first-of-type:pl-4",
              "bg-card group-hover/table-row:bg-secondary",
              /**
               * To ensure the row stays highlighted when "Actions menu" is open for this row.
               */
              "group-has-[[data-row-actions-open='true']]/table-row:bg-secondary",
              {
                "justify-center": isActionsCell,
                "p-0": isActionsCell,
                "-ml-4": isActionsCell,
              }
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );
}

function getPinnedColumnStyles(side: "left" | "right" | false): CSSProperties {
  if (side === false) {
    return {};
  }

  return {
    position: "sticky",
    left: side === "left" ? 0 : undefined,
    right: side === "right" ? 0 : undefined,
  };
}

export function TableFooter({ children }: { children: React.ReactNode }) {
  return (
    <tfoot
      className={cn(
        "sticky bottom-0 left-0 col-span-full grid border-t border-border"
      )}
      style={{
        /**
         * Problem: The `width: 100%` represents the width of the whole table (including the elements that overflow).
         *          This makes is impossible to create sticky footer.
         * Solution: Use the container query units to "escape" the CSS grid in the table footer.
         *           The container represents the table width WITHOUT the overflow.
         * The scrollbar-width variable is to account for the vertical table scrollbar.
         */
        width: `calc(100cqi - var(${TABLE_VERTICAL_SCROLLBAR_OFFSET_CSS_VARIABLE_NAME}))`,
      }}
    >
      <tr className="flex min-h-14 flex-wrap items-center bg-secondary px-4 py-4 sm:py-1">
        <td className="flex w-full flex-row flex-wrap justify-center gap-4 sm:justify-between">
          {children}
        </td>
      </tr>
    </tfoot>
  );
}

function TableColumnResizeHandle({
  onResize,
  isResizing,
}: {
  onResize: (event: unknown) => void;
  isResizing: boolean;
}) {
  return (
    <div
      onMouseDown={onResize}
      onTouchStart={onResize}
      data-resizing={isResizing}
      className={cn(
        "px-4",
        "-mr-4",
        "h-full",
        "flex",
        "items-center",
        "group/resize-handle",
        "hover:cursor-ew-resize"
      )}
    >
      <div className={cn("h-8", "w-1", "bg-transparent", "flex items-center")}>
        <div
          className={cn(
            "w-px",
            "h-6",
            "bg-border",
            "transition-all",
            "group-hover/resize-handle:bg-primary group-data-[resizing=true]/resize-handle:bg-primary",
            "group-hover/resize-handle:h-8 group-data-[resizing=true]/resize-handle:h-8",
            "group-hover/resize-handle:w-1 group-data-[resizing=true]/resize-handle:w-1"
          )}
        />
      </div>
    </div>
  );
}

function useGridTemplateColumns({
  table,
  tableRef,
}: {
  table: Table<TableRecord>;
  tableRef: React.RefObject<HTMLTableElement>;
}) {
  const resizedColumns = useRef<Record<string, number>>({});

  const columnSizingInfo = table.getState().columnSizingInfo;
  const headers = table.getFlatHeaders();

  /**
   * Problem: When there are few columns, we must make sure they take all available space.
   * Solution: Use `minmax` for the columns.
   * Problem: Using `minmax` is good for initial render, but prevents us from resizing the columns.
   * Solution: Custom resize handler to read the width before resizing.
   * Based on https://github.com/TanStack/table/discussions/3192#discussioncomment-8614167
   */
  const gridTemplateColumns = useMemo(() => {
    /**
     * There must at least be one column.
     * That column can't be the "actions" column – it is not possible to configure our table like that.
     */
    if (headers.length === 1) {
      return `minmax(${headers[0].column.getSize()}px, 1fr)`;
    }

    const columnSizes = headers.map((header, index, allHeaders) => {
      const isActionsColumn = header.column.id === TABLE_ROW_ACTIONS_COLUMN_ID;
      if (isActionsColumn) {
        return "min-content";
      }

      const currentColumnSize = header.column.getSize();
      if (header.column.getIsResizing()) {
        resizedColumns.current[header.id] = currentColumnSize;
      }

      /**
       * This column needs to "absorb" the remaining space, when column before it is resized.
       */
      const isLastColumn = index === allHeaders.length - 1;
      if (isLastColumn) {
        return `minmax(${currentColumnSize}px, 1fr)`;
      }

      /**
       * Since the `actions column` is not resizable and sticky, this column has to act as a "last column".
       */
      const nextColumnId = allHeaders[index + 1]?.column.id;
      const isNextColumnActionsColumn =
        nextColumnId === TABLE_ROW_ACTIONS_COLUMN_ID;
      if (isNextColumnActionsColumn) {
        return `minmax(${currentColumnSize}px, 1fr)`;
      }

      const isResizing = header.column.getIsResizing();
      const hasBeenResized = resizedColumns.current[header.id] != null;
      if (isResizing || hasBeenResized) {
        return `${currentColumnSize}px`;
      }

      /**
       * The default for all columns is to fill the remaining space.
       */
      return `minmax(${currentColumnSize}px, 1fr)`;
    });

    return columnSizes.join(" ");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, columnSizingInfo]);

  return {
    gridTemplateColumns: gridTemplateColumns,
    getResizeHandler:
      (header: Header<TableRecord, unknown>) => (event: unknown) => {
        const tableHeaderElement = tableRef.current?.querySelector("thead");
        const tableHeaderCell = tableHeaderElement?.querySelector(
          `[data-column-id="${header.column.id}"]`
        );

        /**
         * We have to read it's width before resizing.
         * This is because this column might be filling the remaining space and might be using `minmax` syntax.
         */
        if (tableHeaderCell) {
          table.setColumnSizing({
            ...resizedColumns.current,
            [header.column.id]: tableHeaderCell.getBoundingClientRect().width,
          });
        }

        /**
         * RAF helps with performance, and ensures we queue the updates in a proper way.
         */
        requestAnimationFrame(() => {
          header.getResizeHandler()(event);
        });
      },
  };
}

export function getTableElementId({ tableId }: { tableId: string }) {
  return `table-${tableId}-table`;
}

export function getTableContainerElementId({ tableId }: { tableId: string }) {
  return `table-${tableId}-container`;
}
